"use client";

import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import { ENTERPRISE_PLAN_URL } from "@workspace/core/constants";
import { CircleCheck, CircleX, Flame } from "lucide-react";
import { useRouter } from "next/navigation";
import { CSSProperties, useState } from "react";

const Check = () => (
  <CircleCheck className="[&>circle]:stroke-[#24a554] [&>circle]:fill-[#24a554] [&>path]:stroke-white" />
);
const X = () => (
  <CircleX className="[&>circle]:stroke-[#E53E3E] [&>circle]:fill-[#E53E3E] [&>path]:stroke-white" />
);

const FEATURES = [
  {
    title: "Users",
    free: "Up to 10",
    team: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    title: "Call Analysis",
    free: "10 / rep",
    team: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    title: "AI Roleplays",
    free: "1 session / rep",
    team: "Unlimited",
    enterprise: "Unlimited",
  },
  {
    title: "Team Dashboard",
    free: "Basic",
    team: "Full",
    enterprise: "Full",
  },
  {
    title: "Manager Insights",
    free: <Check />,
    team: <Check />,
    enterprise: <Check />,
  },
  {
    title: "Risk-Free Trial",
    free: <Check />,
    team: <Check />,
    enterprise: <Check />,
  },
] as const;

const PLANS = [
  { id: "free", title: "Free", price: { monthly: "$0", annual: "$0" } },
  { id: "team", title: "Team", price: { monthly: "$63", annual: "$45" } },
  {
    id: "enterprise",
    title: "Enterprise",
    price: { monthly: "$0", annual: "$0" },
  },
] as const;

export const Comparison = () => {
  const router = useRouter();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "annual",
  );
  const buttonText = (id: "free" | "team" | "enterprise") => {
    const text: Record<typeof id, string> = {
      free: "Sign Up for Free",
      team: "Start Your 30-Day Trial",
      enterprise: "Book a Demo",
    };
    return text[id];
  };
  const handleSubscribe = (id: "free" | "team" | "enterprise") => {
    if (id === "enterprise") {
      return router.push(ENTERPRISE_PLAN_URL);
    }
    return router.push("/auth");
  };
  return (
    <div className="flex flex-col w-full px-5">
      <div className="flex items-start">
        <div className="flex flex-col items-center w-full gap-3 lg:gap-8 lg:items-start lg:w-[354px] min-w-[354px] mb-10 lg:mb-0">
          <span className="text-[32px] font-extrabold leading-8 lg:leading-10">
            Comparison Table
          </span>
          <div className="flex rounded-[16px] border border-d-border px-[5px] py-1 h-fit bg-d-secondary">
            <Button
              variant={billingCycle === "monthly" ? "d-primary" : "ghost"}
              data-state={billingCycle === "monthly" ? "active" : "inactive"}
              onClick={() => setBillingCycle("monthly")}
              className="font-sfpro-rounded text-xl font-semibold data-[state=inactive]:text-d-gray h-12 border-2 data-[state=inactive]:border-transparent data-[state=inactive]:hover:bg-transparent"
            >
              Monthly
            </Button>
            <Button
              variant={billingCycle === "annual" ? "d-primary" : "ghost"}
              data-state={billingCycle === "annual" ? "active" : "inactive"}
              onClick={() => setBillingCycle("annual")}
              className="font-sfpro-rounded text-xl font-semibold data-[state=inactive]:text-d-gray h-12 border-2 data-[state=inactive]:border-transparent data-[state=inactive]:hover:bg-transparent"
            >
              Annually{" "}
              <span className="px-2 py-1 bg-white rounded-[8px] shadow-[0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-[13px] ml-2 border-2 border-[rgba(255,255,255,.08)]">
                29% off
              </span>
            </Button>
          </div>
        </div>
        {PLANS.map((plan, i) => (
          <div
            key={i}
            style={{ "--bc": "#f4e7b1" } as CSSProperties}
            className={cn(
              "hidden lg:flex flex-col bg-white  p-6 w-full h-full",
              {
                "rounded-tl-[20px] border-x border-t border-[var(--bc)]":
                  i === 0,
                "border-t border-[var(--bc)]": i === 1,
                "rounded-tr-[20px] border-x border-t border-[var(--bc)]":
                  i === 2,
              },
            )}
          >
            <span className="text-xl font-semibold flex items-center gap-2 mb-5">
              {plan.title}
              {plan.id === "team" && (
                <span className="flex items-center justify-center size-6 rounded-[6px] bg-[#f3e2c3]">
                  <Flame size={16} className="fill-d-gray stroke-d-gray" />
                </span>
              )}
            </span>
            {plan.id === "enterprise" && (
              <span className="text-[40px] font-extrabold leading-10 mb-3">
                Custom
              </span>
            )}
            {plan.id !== "enterprise" && (
              <div className="flex items-center mb-3">
                {plan.id === "team" && billingCycle === "annual" && (
                  <strong className="text-d-secondary-gray text-[40px] leading-10 font-extrabold mr-2 line-through">
                    {plan.price.monthly}
                  </strong>
                )}
                <strong className="text-[40px] font-extrabold mr-2 leading-10">
                  {plan.price[billingCycle]}
                </strong>
              </div>
            )}
            <span className="text-d-gray text-sm mb-5">user/month</span>
            <Button
              variant="d-primary"
              className="w-full h-12"
              onClick={() => handleSubscribe(plan.id)}
            >
              {buttonText(plan.id)}
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-col border border-[#f4e7b1] rounded-[20px] lg:rounded-tr-none overflow-hidden">
        {FEATURES.map((f, i) => (
          <div
            key={i}
            className="flex flex-wrap w-full lg:h-[72px] bg-white border-b border-[#f4e7b1] last:border-b-0"
          >
            <span className="w-full lg:w-[354px] lg:min-w-[354px] flex items-center px-5 pt-5 pb-2.5 lg:pt-0 lg:pb-0 font-medium text-[#020617]">
              {f.title}
            </span>
            <div className="flex flex-auto items-center px-5 pb-2.5 lg:p-0">
              <span className="w-1/3 lg:w-full flex flex-col lg:items-center justify-center">
                <span className="font-medium leading-6 mb-1 lg:hidden">
                  Free
                </span>
                <span className="font-medium text-d-gray">{f.free}</span>
              </span>
              <span className="w-1/3 lg:w-full flex flex-col lg:items-center justify-center">
                <span className="font-medium leading-6 mb-1 lg:hidden">
                  Team
                </span>
                <span className="font-medium text-d-gray">
                  {i === FEATURES.length - 1 && billingCycle === "monthly" ? (
                    <X />
                  ) : (
                    f.team
                  )}
                </span>
              </span>
              <span className="w-1/3 lg:w-full flex flex-col lg:items-center justify-center">
                <span className="font-medium leading-6 mb-1 lg:hidden">
                  Enterprise
                </span>
                <span className="font-medium text-d-gray">
                  {i === FEATURES.length - 1 && billingCycle === "monthly" ? (
                    <X />
                  ) : (
                    f.enterprise
                  )}
                </span>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
